import { Injectable } from '@angular/core';
import { 
  Firestore, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  doc, 
  getDoc,
  setDoc,
  runTransaction
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, map, of, lastValueFrom, catchError } from 'rxjs';
import { Sticker, StickerCollection } from '../interfaces/sticker.interface';
import { PhotoService } from './photo.service';
import { PointsService } from './points.service';
import { ChildrenService } from './children.service';
import { FirestoreUtilsService } from './firestore-utils.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StickerService {
  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private http: HttpClient,
    private photoService: PhotoService,
    private pointsService: PointsService,
    private childrenService: ChildrenService,
    private firestoreUtils: FirestoreUtilsService
  ) {
    // Force override to ensure we're using Runware
    this.OPENAI_API_URL = null;
    console.log('🔴🔴🔴 INITIALIZING STICKER SERVICE WITH RUNWARE AI - VERSION 1.0 🔴🔴🔴');
    console.log('🔴 RUNWARE API ENDPOINT:', this.RUNWARE_API_URL);
    console.log('🔴 OPENAI HAS BEEN DISABLED:', this.OPENAI_API_URL === null);
    
    // Add a timestamp to ensure this is the latest version
    console.log('🔴 SERVICE INITIALIZED AT:', new Date().toISOString());
  }
  // Runware AI API endpoint
  private readonly RUNWARE_API_URL = 'https://api.runware.ai/v1/image-inference';
  // Completely remove any OpenAI references
  private readonly OPENAI_API_URL = null;
  
  /**
   * Generate a sticker image using Runware AI API
   */
  async generateStickerImage(prompt: string, childId: string, cost: number = 1): Promise<string> {
    console.log('🚀 USING RUNWARE AI FOR IMAGE GENERATION 🚀');
    console.log('generateStickerImage: Generating sticker with prompt:', prompt);
    console.log('generateStickerImage: Child ID:', childId);
    
    // Check if the user is authenticated with Firebase
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to generate a sticker');
    }
    
    console.log('generateStickerImage: Current user:', currentUser.uid);
    console.log('generateStickerImage: Is current user the child?', currentUser.uid === childId);
    
    // IMPORTANT: Check points BEFORE generating the image to save money on API calls
    // Always check the points for the child, not the parent
    console.log('generateStickerImage: Checking points for child ID:', childId);
    
    const userPoints = await this.getUserPoints(childId);
    console.log('generateStickerImage: Child has', userPoints, 'points, sticker costs', cost);
    
    if (userPoints < cost) {
      console.error(`Insufficient points! Child has ${userPoints} points but needs ${cost}`);
      throw new Error('Insufficient points');
    }
    
    try {
      // Only call Runware AI API if the child has enough points
      console.log('generateStickerImage: Child has sufficient points, generating image with RUNWARE AI...');
      const imageData = await this.generateAIImage(prompt);
      
      // NOTE: We don't deduct points here anymore to avoid double deduction
      // Points are now deducted only in saveStickerToCollection
      
      return imageData;
    } catch (error) {
      console.error('Error generating sticker image:', error);
      throw error;
    }
  }

  /**
   * Add a transaction for a child's account, handling the case where a parent is logged in
   * This ensures points are deducted from the child's account, not the parent's
   */
  private async addTransactionForChild(childId: string, amount: number, type: 'earn' | 'spend', description: string): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }
    
    const isCurrentUserChild = currentUser.uid === childId;
    console.log(`addTransactionForChild: Current user: ${currentUser.uid}, Child ID: ${childId}`);
    console.log(`addTransactionForChild: Is current user the child? ${isCurrentUserChild}`);
    
    try {
      // If the current user is the child, use the standard addTransaction method
      if (isCurrentUserChild) {
        console.log(`addTransactionForChild: Child is logged in, using standard transaction`);
        await this.pointsService.addTransaction(amount, type, description);
        return;
      }
      
      // If a parent is logged in, we need to manually update the child's points document
      console.log(`addTransactionForChild: Parent is logged in, manually updating child's points`);
      
      // Use the injected Firestore service for all operations
      const db = this.firestore;
      
      // First, verify if the child exists in the children collection
      const childDocRef = doc(db, `children/${childId}`);
      const childDoc = await getDoc(childDocRef);
      
      if (!childDoc.exists()) {
        console.error(`addTransactionForChild: Child document does not exist for ID: ${childId}`);
        throw new Error('Child not found');
      }
      
      // Determine where the points are stored (either in points collection or children collection)
      const childData = childDoc.data();
      const pointsStoredInChildDoc = childData.hasOwnProperty('points');
      
      console.log(`addTransactionForChild: Points are stored in ${pointsStoredInChildDoc ? 'children' : 'points'} collection`);
      
      // Get current points balance
      let currentPoints = 0;
      
      if (pointsStoredInChildDoc) {
        // Points are stored directly in the child document
        currentPoints = childData['points'] || 0;
      } else {
        // Points are stored in the points collection
        const pointsRef = doc(db, `points/${childId}`);
        const pointsDocSnapshot = await getDoc(pointsRef);
        
        if (pointsDocSnapshot.exists()) {
          currentPoints = pointsDocSnapshot.data()['balance'] || 0;
        } else {
          // If no points document exists, create one with initial balance of 10
          console.log(`addTransactionForChild: Creating new points document for child ${childId} with initial balance of 10`);
          await setDoc(doc(db, `points/${childId}`), {
            balance: 10,
            lastUpdated: new Date()
          });
          currentPoints = 10;
        }
      }
      
      // Calculate new balance
      const newBalance = type === 'earn' ? currentPoints + amount : currentPoints - amount;
      
      console.log(`addTransactionForChild: Current points balance: ${currentPoints}`);
      console.log(`addTransactionForChild: Transaction amount: ${amount}`);
      console.log(`addTransactionForChild: Transaction type: ${type}`);
      console.log(`addTransactionForChild: New balance would be: ${newBalance}`);
      
      if (type === 'spend' && newBalance < 0) {
        console.error(`addTransactionForChild: Insufficient points! Current: ${currentPoints}, Trying to spend: ${amount}`);
        throw new Error('Insufficient points');
      }
      
      // Create a separate function for the transaction to ensure proper context
      const performTransaction = async () => {
        return await runTransaction(db, async (transaction) => {
          // Update points in the appropriate document
          if (pointsStoredInChildDoc) {
            // Update points in the child document
            transaction.update(childDocRef, {
              points: newBalance,
              lastUpdated: new Date()
            });
          } else {
            // Update points in the points collection
            const pointsRef = doc(db, `points/${childId}`);
            transaction.update(pointsRef, {
              balance: newBalance,
              lastUpdated: new Date()
            });
          }
          
          // Add transaction record - IMPORTANT: Include parentId to satisfy security rules
          const transactionsRef = collection(db, 'transactions');
          const newTransactionRef = doc(transactionsRef);
          transaction.set(newTransactionRef, {
            userId: childId,           // The child's ID
            parentId: currentUser.uid, // The parent's ID - required for security rules
            childId: childId,          // Explicitly set childId for clarity
            amount,
            type,
            description,
            timestamp: new Date()
          });
          
          return { success: true };
        });
      };
      
      // Execute the transaction
      await performTransaction();
    } catch (error) {
      console.error('addTransactionForChild: Error adding transaction:', error);
      throw error;
    }
  }

  /**
   * Save a generated sticker to the user's collection
   */
  async saveStickerToCollection(imageData: string, prompt: string, childId: string, isPublic: boolean = false, cost: number = 1): Promise<Sticker> {
    if (!childId) {
      throw new Error('Child ID is required');
    }
    
    // Check if the user is authenticated with Firebase
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to save a sticker');
    }
    
    console.log('saveStickerToCollection: Current authenticated user:', currentUser.uid);
    console.log('saveStickerToCollection: Saving sticker for child:', childId);
    
    // Determine if the current user is a child or parent
    const isCurrentUserChild = currentUser.uid === childId;
    console.log('saveStickerToCollection: Is current user the child?', isCurrentUserChild);
    
    try {
      // Upload image to Firebase Storage
      const timestamp = new Date();
      const imagePath = `stickers/${childId}/${timestamp.getTime()}.jpg`;
      const imageUrl = await this.photoService.uploadPhotoFromDataUrl(imageData, imagePath);
      
      // Get child's name for creator credit if sticker is public
      let creatorName = '';
      if (isPublic) {
        try {
          // Get the child document using the parent-scoped query approach
          // to ensure the security rules are respected
          const childrenQuery = await this.firestoreUtils.createParentScopedQuery('children');
          const childrenQueryWithFilter = query(childrenQuery, where('id', '==', childId));
          const childDocs = await getDocs(childrenQueryWithFilter);
          
          if (!childDocs.empty) {
            const childDoc = childDocs.docs[0];
            creatorName = childDoc.data()['displayName'] || '';
          }
        } catch (error) {
          console.error('Error getting child info for sticker:', error);
          // Continue without the creator name if we can't get it
        }
      }
      
      // Save sticker metadata to Firestore using parent-scoped approach
      // Get the parent ID from the authenticated user
      const parentId = this.auth.currentUser?.uid;
      const stickersRef = collection(this.firestore, 'stickers');
      
      // Create the sticker data with proper security fields to satisfy Firestore security rules
      const stickerData = {
        childId: childId,             // The actual child ID who created the sticker
        parentId: parentId,          // The parent ID is required for security rules filtering
        imageUrl,
        prompt,
        createdAt: timestamp,
        cost,
        public: isPublic,
        creatorName: isPublic ? (creatorName || 'Anonymous') : null // Use null instead of undefined, and provide a fallback name
      } as Omit<Sticker, 'id'>;
      
      const docRef = await addDoc(stickersRef, stickerData);
      
      // Deduct points from the CHILD'S account, not the parent's
      const transactionDescription = isCurrentUserChild ? 
        'Generated a sticker' : 
        `Generated a sticker for ${childId}`;
      
      console.log('saveStickerToCollection: Deducting', cost, 'points with description:', transactionDescription);
      // Use ChildrenService to update points, which will also update the straw-balance in the top bar
      await this.childrenService.adjustPoints(childId, -cost, transactionDescription);
      
      return {
        id: docRef.id,
        ...stickerData
      };
    } catch (error) {
      console.error('Error saving sticker:', error);
      throw error;
    }
  }
  
  /**
   * Generate a new sticker using Runware AI and save it to the user's collection
   * @deprecated Use generateStickerImage and saveStickerToCollection instead
   */
  async generateSticker(prompt: string, childId: string, isPublic: boolean = false, cost: number = 1): Promise<Sticker> {
    const imageData = await this.generateStickerImage(prompt, childId, cost);
    return this.saveStickerToCollection(imageData, prompt, childId, isPublic, cost);
  }
  
  /**
   * Get all stickers created by a specific child
   */
  getChildStickers(childId: string): Observable<Sticker[]> {
    if (!childId) {
      console.log('getChildStickers: No childId provided, returning empty array');
      return of([]);
    }
    
    console.log('getChildStickers: Querying stickers for childId:', childId);
    
    // Get the current authenticated user
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('getChildStickers: No authenticated user found');
      return of([]);
    }
    
    console.log('getChildStickers: Current authenticated user:', currentUser.uid);
    
    const stickersRef = collection(this.firestore, 'stickers');
    
    // Create a query that will find stickers where:
    // 1. The childId matches the current authenticated user (parent) AND actualChildId matches the requested child
    // 2. OR the childId directly matches the requested child (for backward compatibility)
    const q = query(
      stickersRef,
      where('childId', '==', currentUser.uid),
      where('actualChildId', '==', childId),
      orderBy('createdAt', 'desc')
    );
    
    // Execute the query
    return from(getDocs(q)).pipe(
      map(snapshot => {
        console.log('getChildStickers: Query successful, found', snapshot.docs.length, 'stickers');
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Sticker));
      }),
      catchError(error => {
        console.error('getChildStickers: Error querying stickers:', error);
        // If there's an error, try the fallback query for backward compatibility
        console.log('getChildStickers: Trying fallback query with direct childId match');
        
        const fallbackQuery = query(
          stickersRef,
          where('childId', '==', childId),
          orderBy('createdAt', 'desc')
        );
        
        return from(getDocs(fallbackQuery)).pipe(
          map(snapshot => {
            console.log('getChildStickers: Fallback query successful, found', snapshot.docs.length, 'stickers');
            return snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as Sticker));
          }),
          catchError(fallbackError => {
            console.error('getChildStickers: Error in fallback query:', fallbackError);
            // If the collection doesn't exist yet, return an empty array instead of an error
            if (fallbackError.code === 'permission-denied') {
              console.log('getChildStickers: Permission denied, the collection might not exist yet');
              return of([]);
            }
            throw fallbackError;
          })
        );
      })
    );
  }
  
  /**
   * Get all public stickers for the community library
   */
  getCommunityStickers(): Observable<Sticker[]> {
    console.log('getCommunityStickers: Querying public stickers');
    
    const stickersRef = collection(this.firestore, 'stickers');
    const q = query(
      stickersRef,
      where('public', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    return from(getDocs(q)).pipe(
      map(snapshot => {
        console.log('getCommunityStickers: Query successful, found', snapshot.docs.length, 'public stickers');
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Sticker));
      }),
      catchError(error => {
        console.error('getCommunityStickers: Error querying public stickers:', error);
        // If the collection doesn't exist yet, return an empty array instead of an error
        if (error.code === 'permission-denied') {
          console.log('getCommunityStickers: Permission denied, the collection might not exist yet');
          return of([]);
        }
        throw error;
      })
    );
  }
  
  /**
   * Add a sticker from the community library to a child's collection
   */
  async addToCollection(sticker: Sticker, childId: string): Promise<void> {
    // Check if the user is authenticated with Firebase
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to add a sticker to your collection');
    }
    
    console.log('Current authenticated user:', currentUser.uid);
    console.log('Adding sticker to collection for child:', childId);
    
    // Create a new entry in the child's collection that references the original sticker
    const collectionsRef = collection(this.firestore, 'stickerCollections');
    await addDoc(collectionsRef, {
      childId: currentUser.uid, // Use authenticated user ID for Firestore security rules
      actualChildId: childId,   // Store the actual child ID as a separate field
      originalStickerId: sticker.id,
      imageUrl: sticker.imageUrl,
      prompt: sticker.prompt,
      addedAt: new Date(),
      creatorId: sticker.childId,
      creatorName: sticker.creatorName
    });
  }
  
  /**
   * Get all stickers in a child's collection (including those added from community)
   */
  getChildCollection(childId: string): Observable<StickerCollection[]> {
    if (!childId) {
      console.log('getChildCollection: No childId provided, returning empty array');
      return of([]);
    }
    
    console.log('getChildCollection: Querying collection for childId:', childId);
    
    // Get the current authenticated user
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('getChildCollection: No authenticated user found');
      return of([]);
    }
    
    console.log('getChildCollection: Current authenticated user:', currentUser.uid);
    
    const collectionsRef = collection(this.firestore, 'stickerCollections');
    
    // Create a query that will find collection items where:
    // 1. The childId matches the current authenticated user (parent) AND actualChildId matches the requested child
    // 2. OR the childId directly matches the requested child (for backward compatibility)
    const q = query(
      collectionsRef,
      where('childId', '==', currentUser.uid),
      where('actualChildId', '==', childId),
      orderBy('addedAt', 'desc')
    );
    
    return from(getDocs(q)).pipe(
      map(snapshot => {
        console.log('getChildCollection: Query successful, found', snapshot.docs.length, 'collection items');
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as StickerCollection));
      }),
      catchError(error => {
        console.error('getChildCollection: Error querying collection:', error);
        
        // If there's an error, try the fallback query for backward compatibility
        console.log('getChildCollection: Trying fallback query with direct childId match');
        
        const fallbackQuery = query(
          collectionsRef,
          where('childId', '==', childId),
          orderBy('addedAt', 'desc')
        );
        
        return from(getDocs(fallbackQuery)).pipe(
          map(snapshot => {
            console.log('getChildCollection: Fallback query successful, found', snapshot.docs.length, 'collection items');
            return snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as StickerCollection));
          }),
          catchError(fallbackError => {
            console.error('getChildCollection: Error in fallback query:', fallbackError);
            // If the collection doesn't exist yet, return an empty array instead of an error
            if (fallbackError.code === 'permission-denied') {
              console.log('getChildCollection: Permission denied, the collection might not exist yet');
              return of([]);
            }
            throw fallbackError;
          })
        );
      })
    );
  }
  
  /**
   * Generate an image using Runware AI API
   */
  private async generateAIImage(prompt: string): Promise<string> {
    // COMPLETELY NEW IMPLEMENTATION USING RUNWARE AI
    // Timestamp to ensure this is the latest version
    const timestamp = new Date().toISOString();
    console.log(`⭐⭐⭐ RUNWARE AI IMAGE GENERATION STARTED AT ${timestamp} ⭐⭐⭐`);
    console.log('🚫🚫🚫 OPENAI/DALL-E HAS BEEN COMPLETELY REMOVED 🚫🚫🚫');
    console.log('🔴🔴🔴 USING ONLY RUNWARE AI NOW 🔴🔴🔴');
    
    // Add sticker-specific prompt enhancement
    const enhancedPrompt = `${prompt}, sticker style, cartoon, child-friendly, colorful`;
    
    try {
      // Check if we have a Runware API key configured
      if (!environment.runware?.apiKey) {
        console.log('No Runware API key provided in environment. Using placeholder image.');
        return this.getRandomPlaceholderImage(prompt);
      }
      
      // Log Runware configuration
      console.log('🔴 RUNWARE CONFIG:', JSON.stringify({
        timestamp: timestamp,
        apiKeyExists: !!environment.runware?.apiKey,
        apiKeyFirstChars: environment.runware.apiKey.substring(0, 4),
        apiKeyLastChars: environment.runware.apiKey.substring(environment.runware.apiKey.length - 4),
        modelName: environment.runware?.model || 'sd-turbo'
      }), '🔴');
      
      // Get the API key and trim any whitespace
      const apiKey = environment.runware.apiKey.trim();
      
      // Check if we have a valid API key
      if (!apiKey) {
        console.error('Invalid Runware API key format.');
        return this.getRandomPlaceholderImage(prompt);
      }
      
      // Create headers object with API key
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      });
      
      // Generate a unique taskUUID for this request
      const taskUUID = crypto.randomUUID();
      console.log('🔄 Generated taskUUID for Runware request:', taskUUID);
      
      // Limit prompt length to reduce token costs (max 100 characters)
      // const limitedPrompt = enhancedPrompt.length > 100 ? enhancedPrompt.substring(0, 100) : enhancedPrompt;
      const limitedPrompt = enhancedPrompt;

      // Prepare the request payload according to Runware API format using the example provided
      const payload = [{
        taskType: 'imageInference',
        taskUUID: taskUUID,
        outputType: 'dataURI',
        outputFormat: 'PNG',
        positivePrompt: limitedPrompt,
        negativePrompt: environment.runware.negativePrompt || 'nsfw, 3d, monochrome',
        height: 1024,
        width: 1024,
        model: environment.runware.model || 'civitai:267728@345685',
        steps: 40,
        clipSkip: 2,
        lora: [
          {
            model: environment.runware.loraModel || 'civitai:144142@160130',
            weight: 1
          }
        ],
        numberResults: 1
      }];
      
      // Log the API request details
      console.log('🔄 RUNWARE AI API REQUEST DETAILS:');
      console.log('- API URL:', this.RUNWARE_API_URL);
      console.log('- Prompt:', limitedPrompt);
      console.log('- Model:', environment.runware.model || 'sd-turbo');
      console.log('- Request payload:', JSON.stringify(payload, null, 2));
      
      // Make the API request
      console.log('📤 Sending Runware AI API request at', new Date().toISOString());
      const startTime = Date.now();
      
      // Send the HTTP request to Runware
      const response$ = this.http.post<any>(
        this.RUNWARE_API_URL, 
        payload, 
        { 
          headers, 
          observe: 'response',
          responseType: 'json'
        }
      );
      
      // Wait for the response
      const fullResponse = await lastValueFrom(response$);
      const endTime = Date.now();
      
      // Log response details
      console.log(`✅ Runware AI API response received in ${endTime - startTime}ms`);
      console.log('- Response timestamp:', new Date().toISOString());
      console.log('- Status code:', fullResponse.status);
      console.log('- Status text:', fullResponse.statusText);
      
      const response = fullResponse.body;
      
      // Log response structure for debugging
      console.log('- Response structure:', JSON.stringify(response, null, 2));
      
      // Extract the image data from the response
      if (!response || !response.data || response.data.length === 0) {
        throw new Error('No response data received from Runware');
      }
      
      // Check for different output types and extract accordingly
      const result = response.data[0];
      
      if (result.imageDataURI) {
        console.log('✅ Successfully generated image from Runware AI (dataURI format)');
        return result.imageDataURI;
      }
      
      if (result.imageBase64Data) {
        console.log('✅ Successfully generated image from Runware AI (base64 format)');
        return `data:image/png;base64,${result.imageBase64Data}`;
      }
      
      if (result.imageURL) {
        console.log('✅ Successfully generated image from Runware AI (URL format)');
        console.log('- Image URL:', result.imageURL);
        return result.imageURL;
      }
      
      // If we get here, we don't have any usable image data
      console.error('❌ Runware AI response data format unexpected:', response);
      throw new Error('No image data in the Runware AI response');
    } catch (error: any) {
      console.error('❌ Error generating AI image with Runware:', error);
      
      // Log detailed error information
      if (error.status) {
        console.error(`- HTTP Status: ${error.status} ${error.statusText}`);
      }
      
      if (error.error) {
        console.error('- Error details:', JSON.stringify(error.error, null, 2));
      }
      
      if (error.message) {
        console.error('- Error message:', error.message);
      }
      
      // For network errors
      if (error instanceof HttpErrorResponse) {
        console.error('- HTTP Error Response:', {
          url: error.url,
          name: error.name,
          message: error.message,
          status: error.status,
          statusText: error.statusText
        });
      }
      
      // Return a placeholder image instead of throwing an error
      console.log('❌ Runware AI API error - Falling back to placeholder image');
      return this.getRandomPlaceholderImage(prompt);
    }
  }
  
  /**
   * Generate a random placeholder image based on the prompt
   */
  private getRandomPlaceholderImage(prompt: string): string {
    // Create a simple hash of the prompt to get a consistent color for the same prompt
    const hash = prompt.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // Generate a color based on the hash
    const hue = hash % 360;
    const saturation = 70 + (hash % 30); // 70-100%
    const lightness = 40 + (hash % 30);  // 40-70%
    
    // Create a colored square as a placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill background
      ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add some simple shapes based on the prompt
      ctx.fillStyle = `hsl(${(hue + 180) % 360}, ${saturation}%, ${lightness}%)`;
      
      // Draw a shape based on the hash
      const shapeType = hash % 4;
      
      if (shapeType === 0) {
        // Circle
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 4, 0, Math.PI * 2);
        ctx.fill();
      } else if (shapeType === 1) {
        // Square
        ctx.fillRect(canvas.width / 4, canvas.height / 4, canvas.width / 2, canvas.height / 2);
      } else if (shapeType === 2) {
        // Triangle
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2, canvas.height / 4);
        ctx.lineTo(canvas.width / 4, canvas.height * 3 / 4);
        ctx.lineTo(canvas.width * 3 / 4, canvas.height * 3 / 4);
        ctx.closePath();
        ctx.fill();
      } else {
        // Star
        const spikes = 5;
        const outerRadius = canvas.width / 4;
        const innerRadius = canvas.width / 8;
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;
        
        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        
        for (let i = 0; i < spikes; i++) {
          ctx.lineTo(cx + Math.cos(rot) * outerRadius, cy + Math.sin(rot) * outerRadius);
          rot += step;
          ctx.lineTo(cx + Math.cos(rot) * innerRadius, cy + Math.sin(rot) * innerRadius);
          rot += step;
        }
        
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
        ctx.fill();
      }
      
      return canvas.toDataURL('image/png');
    }
    
    // Fallback to a simple colored square if canvas is not supported
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAEXklEQVR4Xu2csU4bQRCGZ3wCJSUlJSVPgHkCQ0mJEEIIofACSAmUQClASYmQEEIIofAEeAI8ASUlJSUlzrF3FhFih7vdnZn9T1pp5d3/m/l2du/OG2ezTJiCQCZUCZUBQoQbAggBQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0ECCEGAFi5dBDgBBiBIiVQw8BQogRIFYOPQQIIUaAWDn0kP9AyMnJST6bzb7kef41y7Jv7XZ7N03Th1ardR/jxVxeXna+1vM1v9Z1v7pZ/FjXbzQaWalUyvr9ftHr9V5i1LVWQi4uLtqFQuHXcrl8yrLsTavVmsdqYF3XmU6n+WQyyRuNRr5YLJ7G4/FzXdfZ9HVrI+Ts7Ky7WCx+5nn+IU3TSYwGSV5zOp3mw+EwbzabT6PR6F7y2pLX0k7I+fn5h/l8/rPdbs8kC9/UtSaTST4YDJ7H4/HtpvNjjddKyGg0+t5ut3/EKD7mNR8eHvLBYDAbjUY/Y15X6tpaCen3++Nutzs/PDyUKnYb13l8fMx7vd7jZDL5vo3rS1xTKyHlbrd7NxwOO7u7uxLFb+0ar6+v+eHh4d10Or3d2kUFL6yVkFKpVJnNZgfVajVJkkSw/O1cKsuyvNPpzObz+XQ2m91s58rvv6pWQpIkKSdJclCr1ZJqtfr+6rd4xvPzc16v1+eLxeJpuVzebLGUd11aLSGVSqUyn88PGo1GUqlU3gVg2yc9PT3l1Wp1Pp/Pp4vF4nrbdby9vnZCkiRJsyw7qNfrSbVa3Qnf+AZCkiSRvpZaQpIkKSdJctBsNpNKpbITvvENhEyn07xWq80Xi8V0uVxeb6CGN4eqJqRUKlXSND2o1+tJkiQ7QYhvIJRKpfl8Pp8ul8tJjHpUE1IsFitZlh00m82kXC7vBCG+gVCv1+fz+Xy6XC4nMepRS0ixWKxkWXZQq9WScrm8E4T4BkKSJPPFYjFdLpeTGPWoJKRQKFTSND2o1WpJqVTaCUJ8A6HZbM4Xi8V0uVxOYtSjkpBCoVDJ8/yg2WwmxWJxJwjxDYR6vT5fLBbT5XI5iVGPOkIKhUKl0+kc7O3tJYVCYScI8Q2EarU6f3l5mS6Xy0mMelQRcnp6Wul0Ogc7QsabCPf39/loNHp+eXmZxHgBVBFyenpaKRaLB7VaLdkRMjYmRB0hGxf8jxOAEGIvBRBCDwFCiBEgVg49BAgRRuA3EvdxQzOvdvUAAAAASUVORK5CYII=';
  }
  
  /**
   * Get the current points balance for a user
   */
  private async getUserPoints(childId: string): Promise<number> {
    console.log('getUserPoints: Getting points for childId:', childId);
    
    try {
      // Get the current authenticated user
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        console.error('getUserPoints: No authenticated user found');
        return 0;
      }
      
      console.log('getUserPoints: Current authenticated user:', currentUser.uid);
      console.log('getUserPoints: Is current user the child?', currentUser.uid === childId);
      
      // Determine if the current user is a child or parent
      const isCurrentUserChild = currentUser.uid === childId;
      
      // IMPORTANT: We need to check the points for the actual child, not the authenticated user
      // If parent is logged in, use the childId parameter
      // If child is logged in, use their own ID (currentUser.uid)
      const pointsCheckId = childId;
      console.log(`getUserPoints: Checking points for ID: ${pointsCheckId} (${isCurrentUserChild ? 'child is logged in' : 'parent is logged in'})`);
      // First check if the child has a points document in the points collection
      const pointsRef = doc(this.firestore, `points/${pointsCheckId}`);
      const pointsDoc = await getDoc(pointsRef);
      
      if (pointsDoc.exists()) {
        const data = pointsDoc.data();
        const points = data['balance'] || 0;
        console.log(`getUserPoints: Found ${points} points in points collection for ${pointsCheckId}`);
        return points;
      }
      
      // If no points document, check the child document as fallback
      const childRef = doc(this.firestore, `children/${pointsCheckId}`);
      const childDoc = await getDoc(childRef);
      
      if (childDoc.exists()) {
        const childData = childDoc.data();
        
        // Check if the child document has points directly
        if (childData.hasOwnProperty('points')) {
          const points = childData['points'] || 0;
          console.log(`getUserPoints: Found ${points} points in child document for ${pointsCheckId}`);
          return points;
        }
      }

      console.log(`getUserPoints: No points found for ${pointsCheckId}, returning 0`);
      return 0;
    } catch (error) {
      console.error('getUserPoints: Error getting points:', error);
      return 0;
    }
  }
}
